import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { api } from '@/utils/api';
import {
  LayoutDashboard, Building2, FileText, Wallet, Wrench, ClipboardCheck,
  MessageSquare, Calendar, Folder, BarChart3, Gauge,
  Users, Shield, Cog, Crown,
  ChevronDown, ChevronsLeft, ChevronsRight,
  Search, Bell, Globe, Menu, LogOut, Settings, Clock, Sparkles, Home,
} from 'lucide-react';
import RevaLogo from '@/components/RevaLogo';
import SandboxBanner from '@/components/SandboxBanner';
import { SIDEBAR_IA, SIDEBAR_ADMIN } from '@/config/sidebarIA';

const ICONS = {
  LayoutDashboard, Building2, FileText, Wallet, Wrench, ClipboardCheck,
  MessageSquare, Calendar, Folder, BarChart3, Gauge,
  Users, Shield, Cog, Crown, Home, Sparkles,
};

const RAIL_W = 64;
const EXPANDED_W = 256;

const tenantMenu = (isAr) => [
  { path: '/tenant',    icon: Home,          label: isAr ? 'بوابتي'    : 'My Portal' },
  { path: '/community', icon: MessageSquare, label: isAr ? 'المجتمع'   : 'Community' },
  { path: '/settings',  icon: Settings,      label: isAr ? 'الإعدادات' : 'Settings' },
];

export default function Layout({ children }) {
  const { user, logout, hasPerm, permissions } = useAuth();
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isAr = i18n.language === 'ar';

  const [mode, setMode] = useState(() => localStorage.getItem('reva_sidebar_mode') || 'rail');
  useEffect(() => { localStorage.setItem('reva_sidebar_mode', mode); }, [mode]);
  // Remove stale group-open accumulator from older REVA versions.
  useEffect(() => { try { localStorage.removeItem('reva_sidebar_open_groups'); } catch { /* silent */ } }, []);
  const expanded = mode === 'expanded';

  const [mobileOpen, setMobileOpen] = useState(false);

  const [hoverGroup, setHoverGroup] = useState(null);
  const hoverTimer = useRef(null);
  const handleHoverEnter = (id) => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHoverGroup(id), 80);
  };
  const handleHoverLeave = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHoverGroup(null), 160);
  };

  // userToggles: explicit per-group user overrides for this session only.
  // - undefined → use default (active group is open, others closed)
  // - true       → user explicitly opened it
  // - false      → user explicitly closed it (even if it's the active group)
  // We intentionally DO NOT persist this to localStorage — stale "open everything"
  // state across sessions was a major source of sidebar clutter.
  const [userToggles, setUserToggles] = useState({});
  const toggleGroup = (id, currentOpen) => {
    setUserToggles(p => ({ ...p, [id]: !currentOpen }));
  };

  const [searchQ, setSearchQ] = useState('');

  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [c, n] = await Promise.all([api.getNotificationCount(), api.getNotifications()]);
        setUnreadCount(c.data.unread_count || 0);
        setNotifications(n.data || []);
      } catch { /* silent */ }
      api.checkAlerts().catch(() => {});
    })();
  }, []);
  const handleMarkAllRead = async () => {
    try { await api.markAllNotificationsRead(); setUnreadCount(0); setNotifications(p => p.map(n => ({ ...n, is_read: true }))); }
    catch { /* silent */ }
  };

  const changeLanguage = (lng) => { i18n.changeLanguage(lng); document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr'; };

  const isTenant = permissions?.role === 'tenant';
  const isPlatformOwner = permissions?.role === 'platform_owner';

  const groups = useMemo(() => {
    if (isTenant) return [];
    return SIDEBAR_IA(isAr)
      .filter(g => isPlatformOwner || hasPerm(g.perm))
      .map(g => ({
        ...g,
        label: isAr ? g.label_ar : g.label_en,
        submenu: g.submenu.map(s => ({ ...s, label: isAr ? s.ar : s.en })),
      }));
  }, [isAr, isTenant, isPlatformOwner, permissions]); // eslint-disable-line

  const adminGroups = useMemo(() => {
    if (isTenant) return [];
    return SIDEBAR_ADMIN(isAr)
      .filter(g => isPlatformOwner || hasPerm(g.perm))
      .map(g => ({ ...g, label: isAr ? g.label_ar : g.label_en }));
  }, [isAr, isTenant, isPlatformOwner, permissions]); // eslint-disable-line

  const q = searchQ.trim().toLowerCase();
  const isMatch = (item) => !q || (item.label || '').toLowerCase().includes(q);

  const isActivePath = (path) => {
    if (!path) return false;
    const [pBase, pSearch = ''] = path.split('?');
    const curBase = location.pathname;
    const curSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search;
    if (curBase !== pBase) return false;
    // For submenu items that include query params we require an EXACT search match.
    // For submenu items without query params, the current page must ALSO have no
    // query params — otherwise both "/tasks" and "/tasks?status=open" would highlight.
    return pSearch === curSearch;
  };
  const isActiveGroup = (g) => isActivePath(g.path) || g.submenu.some(s => isActivePath(s.path));

  const headerTitle = useMemo(() => {
    // Prefer the group whose own path matches the current pathname exactly.
    // Only fall back to submenu match if no parent group is the active route.
    for (const g of groups) if (isActivePath(g.path)) return g.label;
    for (const g of adminGroups) if (isActivePath(g.path)) return g.label;
    for (const g of groups) {
      const sub = g.submenu.find(s => isActivePath(s.path));
      if (sub) return `${g.label} · ${sub.label}`;
    }
    if (location.pathname === '/settings') return isAr ? 'الإعدادات' : 'Settings';
    return 'REVA';
  }, [location.pathname, groups, adminGroups, isAr]); // eslint-disable-line

  const RailButton = ({ g, isActive, onClick }) => {
    const Icon = ICONS[g.icon] || LayoutDashboard;
    return (
      <button
        onClick={onClick}
        data-testid={`sidebar-rail-${g.id || g.path?.replace(/\//g, '')}`}
        className={`relative w-full h-11 flex items-center justify-center group transition-colors ${
          isActive ? 'text-white' : 'text-white/55 hover:text-white'
        }`}
        title={g.label}
      >
        {isActive && <span className="absolute start-0 top-2 bottom-2 w-[3px] rounded-e-full bg-[#D4AF37]" />}
        <span className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${isActive ? 'bg-white/[0.10]' : 'group-hover:bg-white/[0.06]'}`}>
          <Icon size={18} strokeWidth={isActive ? 2 : 1.6} />
          {g.pillar && <span className="absolute -top-0.5 -end-0.5 w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />}
        </span>
      </button>
    );
  };

  const Flyout = ({ g }) => {
    if (!g) return null;
    const Icon = ICONS[g.icon] || LayoutDashboard;
    return (
      <div
        onMouseEnter={() => handleHoverEnter(g.id)}
        onMouseLeave={handleHoverLeave}
        data-testid={`sidebar-flyout-${g.id}`}
        className="absolute top-0 h-full pt-1 pb-1 z-50"
        style={{ [isAr ? 'right' : 'left']: RAIL_W, minWidth: 260 }}
      >
        <div className="rounded-xl shadow-2xl border border-white/[0.07] overflow-hidden"
          style={{ background: 'linear-gradient(180deg, #14181C 0%, #0E1112 100%)' }}>
          <div className="px-4 py-3 border-b border-white/[0.07] flex items-center gap-2.5">
            <Icon size={14} className="text-[#D4AF37]" strokeWidth={1.7} />
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/85 reva-mono">{g.label}</span>
            {g.pillar && <span className="ms-auto text-[9px] font-bold uppercase tracking-[0.18em] text-[#D4AF37] reva-mono">{isAr ? 'مميّز' : 'Pillar'}</span>}
          </div>
          <div className="py-1.5 max-h-[60vh] overflow-y-auto">
            {g.submenu.map(s => {
              const active = isActivePath(s.path);
              return (
                <button
                  key={s.path}
                  data-testid={`sidebar-submenu-${s.path.replace(/[/?=&]/g, '_')}`}
                  onClick={() => { setHoverGroup(null); navigate(s.path); if (mobileOpen) setMobileOpen(false); }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-start text-[12.5px] transition-colors ${
                    active ? 'text-white bg-white/[0.07]' : 'text-white/70 hover:text-white hover:bg-white/[0.05]'
                  }`}
                >
                  <span className="flex-1 truncate">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const InlineGroup = ({ g }) => {
    // Effective open state: user toggle wins; otherwise default = isActiveGroup
    const userPref = userToggles[g.id];
    const open = userPref === undefined ? isActiveGroup(g) : userPref;
    const visible = g.submenu.filter(isMatch);
    if (q && visible.length === 0 && !isMatch(g)) return null;
    const Icon = ICONS[g.icon] || LayoutDashboard;
    const active = isActiveGroup(g);
    return (
      <div className="mb-0.5">
        <button
          onClick={() => toggleGroup(g.id, open)}
          data-testid={`sidebar-group-${g.id}`}
          className={`relative w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors ${
            active ? 'bg-white/[0.08] text-white' : 'text-white/70 hover:bg-white/[0.04] hover:text-white'
          }`}
        >
          {active && <span className="absolute start-0 top-2 bottom-2 w-[3px] rounded-e-full bg-[#D4AF37]" />}
          <Icon size={16} strokeWidth={active ? 2 : 1.7} className={active ? 'text-[#D4AF37]' : ''} />
          <span className="text-[12.5px] font-medium flex-1 text-start truncate">{g.label}</span>
          {g.pillar && <span className="text-[9px] reva-mono uppercase tracking-[0.18em] text-[#D4AF37]/80">★</span>}
          <ChevronDown size={12} className={`transition-transform opacity-60 ${open ? '' : '-rotate-90'}`} />
        </button>
        {open && (
          <div className="ps-9 pe-1 mt-0.5 mb-1.5 space-y-px border-s border-white/[0.06] ms-5">
            {visible.map(s => (
              <button
                key={s.path}
                data-testid={`sidebar-inline-${s.path.replace(/[/?=&]/g, '_')}`}
                onClick={() => navigate(s.path)}
                aria-current={isActivePath(s.path) ? 'page' : undefined}
                className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-[12px] text-start transition-colors ${
                  isActivePath(s.path) ? 'text-white bg-white/[0.07]' : 'text-white/60 hover:text-white hover:bg-white/[0.04]'
                }`}
              >
                <span className="flex-1 truncate">{s.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const sidebarWidth = mobileOpen ? EXPANDED_W : (expanded ? EXPANDED_W : RAIL_W);

  return (
    <div className="min-h-screen reva-light flex" dir={isAr ? 'rtl' : 'ltr'}>
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setMobileOpen(false)} />}

      <aside
        data-testid="reva-sidebar"
        data-mode={expanded ? 'expanded' : 'rail'}
        onMouseLeave={handleHoverLeave}
        className={`${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 fixed md:relative z-40 h-screen transition-all duration-200 flex flex-col`}
        style={{
          width: sidebarWidth,
          background: 'linear-gradient(180deg, #0B0E10 0%, #0F1316 60%, #0A0D10 100%)',
          borderInlineEnd: '1px solid rgba(212,175,55,0.10)',
        }}
      >
        <div className={`flex items-center ${expanded ? 'px-3 py-3.5 justify-between' : 'flex-col py-3 gap-2'}`}>
          <Link to="/" className={expanded ? '' : 'mx-auto'}>
            <RevaLogo variant={expanded ? 'full' : 'icon'} size={expanded ? 30 : 28} theme="dark" glow />
          </Link>
          <button
            onClick={() => setMode(expanded ? 'rail' : 'expanded')}
            data-testid="sidebar-toggle-mode"
            className="p-1.5 hover:bg-white/10 rounded-md text-white/55"
            title={expanded ? (isAr ? 'تصغير' : 'Collapse') : (isAr ? 'توسيع' : 'Expand')}
          >
            {expanded
              ? (isAr ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />)
              : (isAr ? <ChevronsLeft size={14} /> : <ChevronsRight size={14} />)}
          </button>
        </div>

        {expanded && !isTenant && (
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/45" />
              <input
                type="text"
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                placeholder={isAr ? 'بحث...' : 'Search modules...'}
                data-testid="sidebar-search"
                className="w-full ps-8 pe-2 py-1.5 text-xs bg-white/[0.04] hover:bg-white/[0.07] focus:bg-white/[0.08] border border-transparent focus:border-[#D4AF37]/30 rounded-lg outline-none transition-colors text-white placeholder:text-white/40"
              />
            </div>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto px-1.5 pb-2 relative">
          {isTenant ? (
            <div className="space-y-0.5 mt-2">
              {tenantMenu(isAr).map(it => {
                const Icon = it.icon;
                const active = isActivePath(it.path);
                return (
                  <Link key={it.path} to={it.path} data-testid={`sidebar-tenant-${it.path.replace(/\//g, '')}`}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] ${active ? 'bg-white/[0.10] text-white' : 'text-white/65 hover:bg-white/[0.05] hover:text-white'}`}>
                    <Icon size={16} /> {expanded && <span>{it.label}</span>}
                  </Link>
                );
              })}
            </div>
          ) : expanded ? (
            <div className="space-y-px">
              {groups.map(g => <InlineGroup key={g.id} g={g} />)}
              {adminGroups.length > 0 && (
                <div className="mt-4 pt-3 border-t border-white/[0.06]">
                  <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 reva-mono">{isAr ? 'الإدارة' : 'Admin'}</div>
                  {adminGroups.map(it => {
                    const Icon = ICONS[it.icon] || Cog;
                    const active = isActivePath(it.path);
                    return (
                      <Link key={it.path} to={it.path} data-testid={`sidebar-admin-${it.path.replace(/\//g, '')}`}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] ${active ? 'bg-white/[0.10] text-white' : 'text-white/65 hover:bg-white/[0.05] hover:text-white'}`}>
                        <Icon size={16} strokeWidth={active ? 2 : 1.7} className={active ? 'text-[#D4AF37]' : ''} />
                        <span className="flex-1">{it.label}</span>
                        {it.path === '/platform' && <Crown size={11} className="text-[#D4AF37]" />}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-0.5 mt-1">
              {groups.map(g => (
                <div key={g.id} className="relative"
                  onMouseEnter={() => handleHoverEnter(g.id)}
                  onMouseLeave={handleHoverLeave}>
                  <RailButton g={g} isActive={isActiveGroup(g)} onClick={() => navigate(g.path)} />
                  {hoverGroup === g.id && <Flyout g={g} />}
                </div>
              ))}
              {adminGroups.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/[0.06] space-y-0.5">
                  {adminGroups.map(it => (
                    <RailButton key={it.path} g={{ ...it, id: it.path }} isActive={isActivePath(it.path)} onClick={() => navigate(it.path)} />
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="border-t border-white/[0.06] p-2 space-y-0.5">
          {expanded ? (
            <>
              <Link to="/settings" data-testid="sidebar-settings"
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] ${isActivePath('/settings') ? 'bg-white/[0.10] text-white' : 'text-white/65 hover:bg-white/[0.05] hover:text-white'}`}>
                <Settings size={16} /> <span>{isAr ? 'الإعدادات' : 'Settings'}</span>
              </Link>
              <div className="flex items-center gap-2 px-3 py-2.5 mt-1 rounded-lg bg-white/[0.04] border border-white/[0.06]">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A47148] flex items-center justify-center text-[#0A0D14] text-xs font-bold">
                  {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-white truncate">{user?.full_name || 'User'}</div>
                  <div className="text-[10px] text-white/55 truncate reva-mono">{permissions?.role || user?.role}</div>
                </div>
                <button onClick={logout} className="p-1.5 text-white/55 hover:text-red-400 hover:bg-red-500/10 rounded-md" title={isAr ? 'تسجيل الخروج' : 'Logout'} data-testid="sidebar-logout">
                  <LogOut size={14} />
                </button>
              </div>
            </>
          ) : (
            <>
              <Link to="/settings" data-testid="sidebar-settings-rail"
                className={`w-full h-10 flex items-center justify-center rounded-lg ${isActivePath('/settings') ? 'bg-white/[0.10] text-white' : 'text-white/55 hover:text-white hover:bg-white/[0.05]'}`}>
                <Settings size={16} />
              </Link>
              <button onClick={logout} data-testid="sidebar-logout-rail"
                className="w-full h-10 flex items-center justify-center text-white/55 hover:text-red-400 hover:bg-red-500/10 rounded-lg">
                <LogOut size={16} />
              </button>
            </>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        <header className="sticky top-0 z-20 backdrop-blur-md px-4 md:px-6 py-2.5"
          style={{ background: 'rgba(255,255,255,0.94)', borderBottom: '1px solid #E6EAF0', color: '#0E1320' }}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 hover:bg-[#F1F3F6] rounded-lg md:hidden" data-testid="header-menu">
                <Menu size={18} />
              </button>
              <div className="hidden md:flex items-center gap-1.5 text-[11px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#00A86B]" title="System healthy" data-testid="health-dot" />
                <span data-testid="org-name">{permissions?.organization_id === '_platform_' ? 'REVA · PLATFORM' : (user?.organization_name || 'REVA')}</span>
              </div>
              <h2 className="font-heading text-base font-semibold text-[#0E1320] tracking-tight truncate" data-testid="page-title">{headerTitle}</h2>
            </div>

            <div className="flex items-center gap-1">
              <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1.5 bg-[#F4F6F9] rounded-lg text-[#71777E] text-[12px] reva-mono" data-testid="global-search-trigger">
                <Search className="h-3.5 w-3.5" />
                <span>{isAr ? 'بحث...' : 'Search REVA…'}</span>
                <span className="ms-2 text-[10px] bg-white border border-[#E6EAF0] rounded px-1 py-px">⌘K</span>
              </div>

              <div className="relative">
                <button data-testid="notification-bell" onClick={() => setShowNotifs(!showNotifs)}
                  className="relative p-2 hover:bg-[#F1F3F6] rounded-lg transition-colors">
                  <Bell className="h-4 w-4 text-[#4A5263]" strokeWidth={1.5} />
                  {unreadCount > 0 && <span className="absolute top-1 end-1 min-w-[14px] h-[14px] px-1 bg-[#D4AF37] text-[#0A0A0F] text-[9px] font-bold flex items-center justify-center rounded-full">{unreadCount}</span>}
                </button>
                {showNotifs && (
                  <div className="absolute end-0 top-11 w-80 bg-white rounded-xl shadow-lg border border-[#E6EAF0] z-50 max-h-96 overflow-y-auto text-[#0E1320]">
                    <div className="p-3.5 border-b border-[#ECEFF3] flex justify-between items-center">
                      <span className="font-heading font-semibold text-sm">{isAr ? 'الإشعارات' : 'Notifications'}</span>
                      {unreadCount > 0 && <button onClick={handleMarkAllRead} className="text-xs text-[color:var(--reva-signal)] hover:underline">{isAr ? 'قراءة الكل' : 'Mark all read'}</button>}
                    </div>
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-sm text-[#71777E]">{isAr ? 'لا توجد إشعارات' : 'No notifications'}</div>
                    ) : notifications.slice(0, 8).map(n => (
                      <div key={n.id} className={`p-3.5 border-b border-[#ECEFF3] last:border-0 ${!n.is_read ? 'bg-[rgba(0,168,107,0.04)]' : ''}`}>
                        <div className="text-sm font-medium text-[#0E1320]">{isAr && n.title_ar ? n.title_ar : n.title}</div>
                        <div className="text-xs text-[#4A5263] mt-1">{isAr && n.message_ar ? n.message_ar : n.message}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button onClick={() => changeLanguage(isAr ? 'en' : 'ar')} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold text-[#4A5263] hover:text-[#0E1320] hover:bg-[#F1F3F6] rounded-lg transition-colors reva-mono" data-testid="header-lang">
                <Globe className="h-3 w-3" /> {isAr ? 'EN' : 'AR'}
              </button>
            </div>
          </div>
        </header>

        <SandboxBanner />

        <main className="flex-1 p-4 md:p-6 overflow-auto relative">{children}</main>
      </div>
    </div>
  );
}
